import { MatDialogRef } from '@angular/material/dialog';

import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  addEntity,
  entityConfig,
  removeEntity,
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';

import { OpenDialogState } from '@data-access/shared';
import { DIALOG_MODES, AddressBodyPayload, AddressNew } from '@erm/cluster';
import { pipe, switchMap, take } from 'rxjs';
import { OrganizationService } from '../../services';
import { ContactService } from '../../../profiles/services/contact.service';

import { AddressDialogMetaData } from '../../types';
import { convertKeysToCamelCase } from '@shared/utils';

const addressEntityConfig = () =>
  entityConfig({
    collection: '_address',
    entity: type<AddressNew>(),
    selectId: (entity) => entity.id,
  });

export const withAddressFeature = (
  service: OrganizationService | ContactService,
  openDialog: (payload: OpenDialogState) => MatDialogRef<unknown>,
) => {
  return signalStoreFeature(
    withEntities(addressEntityConfig()),
    withMethods(({ ...store }) => {
      /*
       * @ENTITY MUTATIONS
       * */
      const _setAddressEntities = (entities: AddressNew[]) => {
        patchState(store, setAllEntities(entities, addressEntityConfig()));
      };
      const _addAddressEntity = (entity: AddressNew) => {
        patchState(store, addEntity(entity, addressEntityConfig()));
      };
      const _updateAddressEntities = (entity: AddressNew) => {
        patchState(
          store,
          updateEntity(
            {
              changes: { ...entity },
              id: entity.id,
            },
            addressEntityConfig(),
          ),
        );
      };

      const _removeAddressFromEntity = (addressId: string) => {
        patchState(store, removeEntity(addressId, addressEntityConfig()));
      };
      /*
       * @ENTITY MUTATIONS END
       * */

      /*
       * @DATA API METHODS END
       * */
      const _postAddress = rxMethod<{
        data: AddressBodyPayload;
        entityId: string;
      }>(
        pipe(
          switchMap((params) => {
            if (service instanceof OrganizationService) {
              return service.addAddress(params.entityId, params.data).pipe(
                tapResponse({
                  error: (error: Error) =>
                    console.error('Error adding address:', error),
                  next: (response) =>
                    _addAddressEntity(
                      convertKeysToCamelCase(response) as AddressNew,
                    ),
                }),
              );
            } else if (service instanceof ContactService) {
              return service.addAddress(params.entityId, params.data).pipe(
                tapResponse({
                  error: (error: Error) =>
                    console.error('Error adding address:', error),
                  next: (response) =>
                    _addAddressEntity(
                      convertKeysToCamelCase(response) as AddressNew,
                    ),
                }),
              );
            }
            throw new Error('Unknown service type');
          }),
        ),
      );

      const _editAddress = rxMethod<{
        data: AddressNew;
        entityId: string;
      }>(
        pipe(
          switchMap((params) => {
            const payload = () =>
              ({
                address1: params.data.address1,
                address2: params.data.address2,
                address3: params.data.address3,
                city: params.data.city,
                countryId: params.data.country.id,
                id: params.data.id,
                state: params.data.state,
                zipCode: params.data.zipCode,
              }) as AddressBodyPayload;

            if (service instanceof OrganizationService) {
              return service.updateAddress(params.entityId, payload()).pipe(
                tapResponse({
                  error: (error: Error) =>
                    console.error('Error updating address', error),
                  next: () => _updateAddressEntities(params.data),
                }),
              );
            } else if (service instanceof ContactService) {
              return service
                .updateAddress(params.entityId, params.data.id, payload())
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      console.error('Error updating address', error),
                    next: () => _updateAddressEntities(params.data),
                  }),
                );
            }
            throw new Error('Unknown service type');
          }),
        ),
      );

      const _removeAddress = rxMethod<{
        entityId: string;
        addressId: string;
      }>(
        pipe(
          switchMap((params) => {
            if (service instanceof OrganizationService) {
              return service
                .removeAddress(params.entityId, params.addressId)
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      console.error('Error removing address', error),
                    next: () => _removeAddressFromEntity(params.addressId),
                  }),
                );
            } else if (service instanceof ContactService) {
              return service
                .deleteAddress(params.entityId, params.addressId)
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      console.error('Error removing address', error),
                    next: () => _removeAddressFromEntity(params.addressId),
                  }),
                );
            }
            throw new Error('Unknown service type');
          }),
        ),
      );
      /*
       * @DATA API METHODS END
       * */

      /*
       * @DIALOG SUBSCRIPTION
       * */
      const _loadDialog = (dialogMetaData: AddressDialogMetaData) => {
        const dialogRef = openDialog(dialogMetaData.payload);
        if (!dialogRef) {
          return;
        }
        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe({
            next: (data: AddressBodyPayload) => {
              if (data) {
                switch (dialogMetaData.mode) {
                  case DIALOG_MODES.CREATE:
                    _postAddress({
                      data: {
                        ...data,
                        addressType: dialogMetaData.addressType,
                      },
                      entityId: dialogMetaData.entityId!,
                    });
                    break;
                  case DIALOG_MODES.EDIT:
                    _editAddress({
                      data: data as unknown as AddressNew,
                      entityId: dialogMetaData.entityId!,
                    });
                    break;
                  case DIALOG_MODES.DELETE:
                    _removeAddress({
                      addressId: dialogMetaData.addressId!,
                      entityId: dialogMetaData.entityId!,
                    });
                }
              }
            },
          });
      };
      /*
       * @DIALOG SUBSCRIPTION END
       * */

      return {
        loadAddressDialog: (dialogMetaData: AddressDialogMetaData) =>
          _loadDialog({ ...dialogMetaData }),
        setAddresses: (entities: AddressNew[]) => _setAddressEntities(entities),
      };
    }),
  );
};
